import type { TokenRequest } from "ably"
import { placeOf, settleNewerPage, spliceRow } from "./newestFirstPages"
import type { Pages } from "./pages"
import type { Recipe, SyncTools } from "./syncPages"
import syncPages, { BUSY_RETRY_MS } from "./syncPages"
import type { UpdateableEntity, Uuid } from "@/utils"

/** Gathers the inserts of a burst into one fetch. */
const NEWER_DELAY_MS = 300

type Args<TItem extends UpdateableEntity> = {
  channel: string
  event: string
  getAblyToken: () => Promise<TokenRequest>
  /** Whether a change concerns this list at all, e.g. a run of the same prompt. */
  isRelevant: (row: TItem) => boolean
  /** Whether the server would list a row, for one spliced in without asking it. */
  isVisible: (row: TItem, actorId: Uuid | null) => boolean
  /** The cached pages, and whether any fetch of them is in flight. */
  getCache: () => { pages: Pages<TItem> | undefined; isFetching: boolean }
  update: (recipe: Recipe<TItem>) => void
  /** Fetches the page before the first one; resolves, never rejects, once it has settled. */
  fetchNewer: () => PromiseLike<unknown>
  /** Refetches the first page; resolves, never rejects. */
  refetchHead: () => PromiseLike<unknown>
  removed: Promise<unknown>
}

/**
 * Keeps an infinite list of newest-first, UUIDv7-ordered rows live from the
 * realtime channel, for an RTK Query `onCacheEntryAdded`. Resolves once the
 * cache entry is removed.
 */
export default async function syncNewestFirstPages<
  TItem extends UpdateableEntity,
>({
  channel,
  event,
  getAblyToken,
  isRelevant,
  isVisible,
  getCache,
  update,
  fetchNewer,
  refetchHead,
  removed,
}: Args<TItem>) {
  let newerTimer: ReturnType<typeof setTimeout> | undefined
  const clearNewer = () => {
    clearTimeout(newerTimer)
  }

  const loadNewer = async (tools: SyncTools<TItem>): Promise<void> => {
    if (tools.isRemoved()) {
      return
    }
    const { pages, isFetching } = getCache()
    if (isFetching) {
      scheduleNewer(tools, BUSY_RETRY_MS)

      return
    }

    // An empty list has no cursor to go back from.
    if (!pages?.pages[0]?.prevCursor) {
      await refetchHead()

      return
    }

    const pageCount = pages.pages.length
    await fetchNewer()
    const landed = getCache().pages
    // A failed fetch prepends nothing; the next insert tries again.
    if (tools.isRemoved() || landed?.pages.length !== pageCount + 1) {
      return
    }

    tools.flushReplay()
    const hasPrev = landed.pages[0]?.hasPrev
    update(settleNewerPage)
    // More than a page arrived: keep going until the head.
    if (hasPrev) {
      await loadNewer(tools)
    }
  }

  const scheduleNewer = (tools: SyncTools<TItem>, delay: number) => {
    if (!newerTimer && !tools.isRemoved()) {
      tools.onRemoved(clearNewer)
      newerTimer = setTimeout(() => {
        newerTimer = undefined
        void loadNewer(tools)
      }, delay)
    }
  }

  await syncPages({
    channel,
    event,
    getAblyToken,
    isRelevant,
    getCache,
    update,
    removed,
    onInsert: (row, actorId, tools) => {
      const { pages } = getCache()
      const place = pages ? placeOf(pages, row) : "empty"
      if (place === "empty" || place === "newer") {
        scheduleNewer(tools, NEWER_DELAY_MS)
      } else if (
        (place === "between" || place === "older") &&
        isVisible(row, actorId)
      ) {
        tools.apply((current) => {
          spliceRow(current, row)
        })
      }
    },
  })
}
